import { gql } from "urql";

const THRESHOLD_VERSION_FIELDS = `
  thresholdPassing
  thresholdPassingWithOverride
  thresholdOverriddenAt
  thresholdOverrideReason
  thresholdOverriddenBy { id fullName }
  thresholdFailures { type dimensionId dimensionName score overall count min }
`;

// Fields read by AssessmentSummaryPanel (the full assessment slideover view).
// Interpolated into every query that feeds a surface that opens the panel,
// so a future field addition is one edit, not five.
const ASSESSMENT_VERSION_FOR_PANEL_FIELDS = `
  id
  overallScore
  recommendation
  overallSummary
  strengths
  concerns
  culturalFitNotes
  trajectoryNotes
  riskNotes
  versionNumber
  effectiveFrom
  structuralFitOverallScore
  structuralFitDimensionCoverage {
    briefFieldKey
    briefFieldLabel
    coveringDimensions { id continuityId name }
  }
  assessor { id fullName }
  generationRun {
    id
    inputCount
    modelUsed
    finishedAt
  }
  ${THRESHOLD_VERSION_FIELDS}
  competencyScores {
    id
    score
    anchorLabel
    evidence
    notes
    detailedNotes
    capabilityDimension {
      id
      continuityId
      name
      description
      idealScore
    }
    behaviouralAnchors {
      id
      evidenceText
      sourceExcerpt
      sourceArtefactType
      sourceArtefactId
      aiGenerated
      createdAt
    }
  }
  structuralFitScores {
    id
    briefFieldKey
    briefFieldLabel
    briefFieldValueExcerpt
    score
    anchorLabel
    evidence
  }
`;

// Story #2395 — "How was this determined?" panel. Run metadata and input
// references only; the API never exposes prompts or wire payloads.
const PROVENANCE_GRAPH_FIELDS = `
  quality
  generationRun {
    id
    workflowName
    modelUsed
    finishedAt
    triggerSource
    chatThreadId
    user { id fullName }
    backgroundTask { id kind title state }
  }
  nodes {
    id
    artefactType
    artefactId
    label
    subLabel
    versionNumber
    origin
    actorSource
    aiGenerated
    generatedAt
    stale
    staleReasons
    inputRole
    provenanceQuality
  }
  edges { id from to kind state sourceExcerpt }
  notIncluded { artefactType artefactId label subLabel reason inputRole }
  notIncludedCount
`;

export const ASSESSMENT_PROVENANCE_QUERY = gql`
  query AssessmentProvenance($id: ID!) {
    campaignCandidate(id: $id) {
      id
      campaign { id }
      candidate { id }
      candidateAssessment {
        id
        currentVersion {
          id
          provenance { ${PROVENANCE_GRAPH_FIELDS} }
        }
      }
    }
  }
`;

export const SYNTHESIS_PROVENANCE_QUERY = gql`
  query SynthesisProvenance($id: ID!) {
    campaignCandidate(id: $id) {
      id
      campaign { id }
      candidateSelectionSteps {
        id
        interviewRoundSynthesis {
          id
          currentVersion {
            id
            provenance { ${PROVENANCE_GRAPH_FIELDS} }
          }
        }
      }
    }
  }
`;

export const DECISION_SUMMARY_PROVENANCE_QUERY = gql`
  query DecisionSummaryProvenance($id: ID!) {
    campaign(id: $id) {
      id
      decisionSummary {
        id
        provenance { ${PROVENANCE_GRAPH_FIELDS} }
      }
    }
  }
`;

export const CURRENT_USER_QUERY = gql`
  query CurrentUser {
    currentUser {
      id
      firstName
      lastName
      fullName
      email
      superuser
    }
    currentOrganisation {
      id
      name
      slug
    }
    currentMembership {
      role
      superuserSynthetic
      organisation {
        id
        name
      }
    }
    organisations {
      id
      name
      slug
    }
  }
`;

export const BACKGROUND_TASKS_QUERY = gql`
  query BackgroundTasks {
    backgroundTasks {
      id
      kind
      title
      state
      subjectType
      subjectId
      parentTaskId
      errorMessage
      result
      startedAt
      finishedAt
      updatedAt
    }
  }
`;

export const ALL_ORGANISATIONS_QUERY = gql`
  query AllOrganisations {
    allOrganisations {
      id
      name
      slug
    }
  }
`;

export const SUPERUSER_AUDIT_QUERY = gql`
  query SuperuserAuditEvents($limit: Int) {
    superuserAuditEvents(limit: $limit) {
      id
      actionType
      targetResource
      targetId
      httpMethod
      path
      graphqlMutation
      performedAt
      metadata
      superuser {
        id
        fullName
        email
      }
    }
  }
`;

export const DOCUMENT_EXPORT_QUERY = gql`
  query DocumentExport($id: ID!) {
    documentExport(id: $id) {
      id
      status
      kind
      googleDocUrl
      errorMessage
    }
  }
`;

export const CURRENT_USER_GOOGLE_INTEGRATION_QUERY = gql`
  query CurrentUserGoogleIntegration {
    currentUserGoogleIntegration {
      id
      googleEmail
      connectedAt
    }
  }
`;

export const GOOGLE_DRIVE_PICKER_QUERY = gql`
  query GoogleDrivePicker {
    googleDrivePicker {
      accessToken
      tokenExpiresAt
      appId
      developerKey
    }
  }
`;

export const CAMPAIGNS_QUERY = gql`
  query Campaigns(
    $status: String
    $archived: Boolean
    $sortBy: CampaignSortByEnum
    $sortDirection: SortDirectionEnum
    $first: Int
    $after: String
  ) {
    campaigns(
      status: $status
      archived: $archived
      sortBy: $sortBy
      sortDirection: $sortDirection
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          title
          status
          currentStage
          candidateCount
          archivedAt
          client {
            id
            name
          }
          createdBy {
            id
            fullName
          }
          updatedAt
          lastAccessedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const CAMPAIGN_QUERY = gql`
  query Campaign($id: ID!) {
    campaign(id: $id) {
      id
      title
      status
      archivedAt
      archiveReason
      currentStage
      candidateCount
      scoredCandidateCount
      client {
        id
        name
        industry
      }
      createdBy {
        id
        fullName
      }
      teamMembers {
        id
        fullName
      }
      attachedNotes {
        id
        content
        noteType
        visibility
        author {
          id
          fullName
        }
        createdAt
      }
      roleBlueprint {
        id
      }
      portalUsers {
        id
        name
        email
        createdAt
      }
      portalInvitations {
        id
        email
        name
        expiresAt
        accepted
        createdAt
      }
      stageActions {
        id
        stage
        actionType
        label
        icon
        handlerType
        config
        position
        disabled
        disabledReason
      }
      campaignSuggestions {
        id
        suggestionType
        content
        status
        createdAt
      }
      artefacts {
        id
        kind
        group
        title
        status
        versionCount
        updatedAt
        url
        outOfSync
      }
      brief {
        id
        currentVersion {
          id
          targetStartDate
        }
      }
      activityEvents {
        id
        kind
        occurredAt
        summary
        details
        actor {
          id
          fullName
        }
      }
      decisionSummary {
        id
        strengthSignal
        keyGap
        recommendedAction
        model
        generatedAt
        stale
      }
      workflowStatus {
        kind
        label
        status
        blockedReason
        locked
        hasLockGate
        dependencies
        phase
      }
      createdAt
      updatedAt
    }
  }
`;

export const CLIENTS_QUERY = gql`
  query Clients($first: Int, $after: String) {
    clients(first: $first, after: $after) {
      edges {
        node {
          id
          name
          industry
          website
          contacts {
            id
            name
            title
            isPrimary
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const CLIENT_QUERY = gql`
  query Client($id: ID!) {
    client(id: $id) {
      id
      name
      industry
      website
      notes
      branding
      preferences
      logoUrl
      searchCountryCodes
      searchLocations {
        name
        countryCode
      }
      canDelete
      contacts {
        id
        name
        title
        email
        phone
        isPrimary
      }
      attachedNotes {
        id
        content
        noteType
        visibility
        author {
          id
          fullName
        }
        createdAt
      }
      campaigns {
        id
        title
        status
      }
    }
  }
`;

export const CAMPAIGN_BRIEF_QUERY = gql`
  query CampaignBrief($id: ID!) {
    campaign(id: $id) {
      id
      selectionProcessCoverage {
        dimensions {
          continuityId
          name
          position
          totalDepth
          coveredStepCount
          uncovered
        }
        steps {
          id
          continuityId
          position
          label
          stepType
          stepTypeLabel
          dimensionsCovered
          averageDepth
          planAcknowledgedAt
        }
        cells {
          dimensionContinuityId
          stepContinuityId
          depth
        }
      }
      teamMembers {
        id
        fullName
      }
      roleBlueprint {
        id
        locked
      }
      assessmentMatrix {
        id
        outOfSync
        dimensions {
          id
          continuityId
          name
          position
        }
      }
      brief {
        id
        stakeholders {
          id
          name
          stakeholderRole
          position
          userId
          user {
            id
            fullName
          }
        }
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
        state
        personas {
          id
          name
          summary
          description
          position
        }
        selectionProcess {
          id
          description
          currentVersion {
            id
            versionNumber
            origin
          }
          steps {
            id
            continuityId
            stepType
            label
            position
            changeReason
            stepFormat
            durationMinutes
            participants {
              role
              name
              organisationMembershipId
            }
            purpose
            origin
            inFlightCandidateCount
            stepAssessmentPlan {
              id
              selectionStepContinuityId
              intent
              coverage {
                dimensionId
                depth
                dimensionName
                dimensionInCurrentMatrix
              }
              currentVersion {
                id
                versionNumber
                origin
                acknowledgedAt
              }
            }
          }
          archivedSteps {
            continuityId
            lastLabel
            lastStepType
            removedAt
            removedBy
            changeReason
            inFlightCandidateCount
          }
        }
        currentVersion {
          id
          additionalNotes
          versionNumber
          author {
            id
            fullName
          }
          effectiveFrom
          effectiveTo
          changeSummary
          salaryBase
          salaryBonus
          salaryEquity
          salaryBenefits
          salaryCurrency
          targetStartDate
          assessmentProcess
          backgroundCheckRequirements
          roleTitle
          roleMandate
          contextNotes
          successOutcomes
          culturalFitNotes
          motivationFitNotes
          rawBriefNotes
          searchCountryCodes
          searchLocations {
            name
            countryCode
          }
        }
        versions {
          id
          additionalNotes
          versionNumber
          author {
            id
            fullName
          }
          effectiveFrom
          effectiveTo
          changeSummary
          salaryBase
          salaryBonus
          salaryEquity
          salaryBenefits
          salaryCurrency
          targetStartDate
          assessmentProcess
          backgroundCheckRequirements
          roleTitle
          roleMandate
          contextNotes
          successOutcomes
          culturalFitNotes
          motivationFitNotes
          rawBriefNotes
          searchCountryCodes
          searchLocations {
            name
            countryCode
          }
        }
      }
    }
  }
`;

export const ORGANISATION_SETTINGS_QUERY = gql`
  query OrganisationSettings {
    currentOrganisation {
      id
      name
      currentUserRole
      branding
      logoUrl
      searchCountryCodes
      searchLocations {
        name
        countryCode
      }
      aiPositiveInstructions
      aiNegativeInstructions
      llmModel
      residencyRequirement
      aiAvailableModels {
        label
        models {
          id
          label
        }
      }
      members {
        id
        user {
          id
          fullName
          email
          superuser
        }
        role
        createdAt
      }
      pendingInvitations {
        id
        email
        role
        expiresAt
        invitedBy {
          id
          fullName
        }
        createdAt
      }
      piiMaskingEnabled
      notifyAdminsOnSuperuserAction
      dataRetentionPolicies {
        id
        resourceType
        retentionDays
        enabled
      }
    }
  }
`;

export const CASCADE_ARTEFACTS_QUERY = gql`
  query CascadeArtefacts($id: ID!) {
    campaign(id: $id) {
      id
      screeningCriteria {
        id
        currentVersion {
          id
          generationState
          generationError
          roleBlueprintVersionNumber
        }
      }
      interviewGuides {
        id
        selectionStepId
        stepType
        currentVersion {
          id
          generationState
          generationError
          roleBlueprintVersionNumber
        }
      }
      assessmentGuide {
        id
        currentVersion {
          id
          generationState
          generationError
        }
      }
      assessmentMatrix {
        id
        currentVersion {
          id
          generationState
          generationError
          roleBlueprintVersionNumber
        }
      }
      candidateScoringMatrix {
        id
        currentVersion {
          id
          generationState
          generationError
          roleBlueprintVersionNumber
        }
      }
    }
  }
`;

export const STALE_ARTEFACTS_QUERY = gql`
  query StaleArtefacts($id: ID!) {
    campaign(id: $id) {
      id
      staleArtefacts {
        kind
        name
        reason
        upstreamKind
        upstreamLabel
        pinnedVersionId
        currentVersionId
        userEdited
        artefactId
        selectionStepContinuityId
      }
    }
  }
`;

export const REGENERATE_PREVIEW_QUERY = gql`
  query RegeneratePreview($id: ID!, $kinds: [String!]!) {
    campaign(id: $id) {
      id
      regeneratePreview(kinds: $kinds) {
        campaignId
        items {
          kind
          name
          reason
          upstreamKind
          upstreamLabel
          pinnedVersionId
          currentVersionId
          userEdited
          artefactId
          selectionStepContinuityId
        }
        manualEditsPresent
        totalJobs
      }
    }
  }
`;

export const CANDIDATE_SCORING_MATRIX_PAGE_QUERY = gql`
  query CandidateScoringMatrixPage($id: ID!) {
    campaign(id: $id) {
      id
      title
      roleBlueprint {
        id
        locked
        currentVersion {
          id
          versionNumber
        }
        capabilityDimensions {
          id
          continuityId
          name
          idealScore
        }
      }
      assessmentMatrix {
        id
        locked
        outOfSync
        currentVersion {
          id
          versionNumber
          roleBlueprintVersionNumber
        }
      }
      campaignCandidates {
        id
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        candidateAssessment {
          id
          currentVersion {
            ${ASSESSMENT_VERSION_FOR_PANEL_FIELDS}
          }
        }
      }
      candidateScoringMatrix {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
        state
        currentVersion {
          id
          versionNumber
          generationState
          generationError
          roleBlueprintVersionNumber
          effectiveFrom
        }
        dimensions {
          id
          name
          idealScore
          critical
        }
        rows {
          campaignCandidateId
          candidateId
          candidateName
          referenceNumber
          overallScore
          thresholdPassing
          assessmentVersionId
          cells {
            capabilityDimensionId
            competencyScoreId
            score
            evidence
            anchorLabel
            idealScore
            flagsThreshold
            stale
            staleReasons
            currentBandBody
          }
        }
        ineligibleCandidatesForScoring {
          campaignCandidateId
          candidateId
          name
        }
      }
    }
  }
`;

export const ASSESSMENT_MATRIX_PAGE_QUERY = gql`
  query AssessmentMatrixPage($id: ID!) {
    campaign(id: $id) {
      id
      title
      roleBlueprint {
        id
        locked
      }
      assessmentGuide {
        id
        totalVersions
        currentVersion {
          id
          content
          versionNumber
          changeSummary
          effectiveFrom
          generationState
          generationError
          model
          assessmentMatrixVersionNumber
          author {
            id
            fullName
          }
        }
        versions {
          id
          versionNumber
          changeSummary
          effectiveFrom
          author {
            id
            fullName
          }
        }
      }
      assessmentMatrix {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
        state
        outOfSync
        currentVersion {
          id
          versionNumber
          generationState
          generationError
          roleBlueprintVersionId
          roleBlueprintVersionNumber
          effectiveFrom
          model
        }
        dimensions {
          id
          name
          idealScore
          critical
        }
        bands {
          id
          capabilityDimensionId
          level
          levelLabel
          body
          position
          effectiveFrom
        }
      }
    }
  }
`;

export const ROLE_BLUEPRINT_QUERY = gql`
  query RoleBlueprint($id: ID!) {
    campaign(id: $id) {
      id
      outOfSyncArtefacts {
        id
        kind
        name
        pinnedVersionId
        currentBlueprintVersionId
      }
      screeningCriteria {
        id
        locked
        state
        currentVersion {
          id
          versionNumber
          effectiveFrom
          changeSummary
          mustHaveCriteria
          niceToHaveCriteria
          exclusions
          generationState
          generationError
          model
          roleBlueprintVersionNumber
          author {
            id
            fullName
          }
        }
      }
      linkedBlueprintArtefacts {
        id
        kind
        name
        pinnedVersionId
        pinnedVersionNumber
        currentBlueprintVersionId
        currentBlueprintVersionNumber
        outOfSync
      }
      roleBlueprint {
        id
        locked
        lockedAt
        lockedBy {
          id
          fullName
        }
        clientApproved
        clientApprovedAt
        clientApprovedBy
        state
        lockable
        hasUncommittedEdits
        uncoveredBriefFields
        lockPreconditions {
          key
          label
          met
          detail
        }
        lockEvents {
          id
          kind
          versionNumber
          occurredAt
          reason
          actor {
            id
            fullName
          }
        }
        capabilityDimensions {
          id
          continuityId
          name
          description
          position
          idealScore
          positiveIndicators
          negativeIndicators
          successCriteria
          critical
          coveredBriefFields
        }
        hireThresholdRule {
          id
          minScoreForCriticalDimensions
          minOverallScore
          minStrengthsCount
          default
        }
        currentVersion {
          id
          versionNumber
          effectiveFrom
          effectiveTo
          changeSummary
        }
        lastLockedVersion {
          id
          versionNumber
          effectiveFrom
          effectiveTo
          changeSummary
          capabilityDimensions {
            id
            continuityId
            name
            description
            position
            idealScore
            positiveIndicators
            negativeIndicators
          }
        }
        versions {
          id
          versionNumber
          effectiveFrom
          effectiveTo
          changeSummary
          superseded
          capabilityDimensions {
            id
            continuityId
            name
            description
            position
            idealScore
            positiveIndicators
            negativeIndicators
          }
        }
      }
    }
  }
`;

export const ASSESSMENT_STAGE_QUERY = gql`
  query AssessmentStage($id: ID!) {
    campaign(id: $id) {
      id
      assessmentGuide {
        id
        totalVersions
        currentVersion {
          id
          content
          versionNumber
          changeSummary
          effectiveFrom
          generationState
          generationError
          model
          assessmentMatrixVersionNumber
          author {
            id
            fullName
          }
        }
        versions {
          id
          versionNumber
          changeSummary
          effectiveFrom
          author {
            id
            fullName
          }
        }
      }
      roleBlueprint {
        id
        locked
        capabilityDimensions {
          id
          continuityId
          name
          description
          idealScore
          positiveIndicators
          negativeIndicators
          critical
          coveredBriefFields
        }
        hireThresholdRule {
          id
          minScoreForCriticalDimensions
          minOverallScore
          minStrengthsCount
          default
        }
      }
      campaignCandidates(stage: "assessment") {
        id
        stage
        createdAt
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        addedBy {
          id
          fullName
        }
        candidateAssessment {
          id
          currentVersion {
            ${ASSESSMENT_VERSION_FOR_PANEL_FIELDS}
            uncoveredBriefFields
          }
        }
      }
    }
  }
`;

export const SHORTLIST_CANDIDATES_QUERY = gql`
  query ShortlistCandidates($id: ID!) {
    campaign(id: $id) {
      id
      currentStage
      campaignCandidates(stage: "shortlist") {
        id
        stage
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        candidateAssessment {
          id
          currentVersion {
            ${ASSESSMENT_VERSION_FOR_PANEL_FIELDS}
          }
        }
      }
    }
  }
`;

export const MARKET_MAP_QUERY = gql`
  query MarketMap($id: ID!) {
    campaign(id: $id) {
      id
      resolvedSearchCountryCodes
      resolvedSearchLocations {
        name
        countryCode
      }
      marketMap {
        id
        notes
        searchQueries
        targetSectors {
          id
          name
          relevance
          position
          notes
          excluded
          targetCompanies {
            id
            name
          }
        }
        targetCompanies {
          id
          name
          relevance
          employeeCountRange
          website
          notes
          excluded
          targetSector {
            id
            name
          }
        }
        xraySearchRuns {
          id
          searchQueryId
          queryString
          status
          resultCount
          newCount
          rediscoveredCount
          dismissedCount
          errorMessage
          countryCode
          locationName
          startedAt
          finishedAt
          createdAt
          discoveredProfiles {
            id
            linkedinUrl
            title
            snippet
            candidateId
            imported
            createdAt
            locationMatch
            locationMatchSource
            inferredLocation
            matchedCandidateId
            matchType
            matchedCandidateRef
          }
        }
      }
    }
  }
`;

export const CAMPAIGN_CANDIDATES_QUERY = gql`
  query CampaignCandidates($id: ID!, $stage: String, $stages: [String!]) {
    campaign(id: $id) {
      id
      campaignCandidates(stage: $stage, stages: $stages) {
        id
        stage
        source
        notes
        createdAt
        candidate {
          id
          referenceNumber
          firstName
          lastName
          fullName
          email
          currentRoleTitle
          currentCompany
          location
          linkedinUrl
          resumes {
            id
            filename
            url
            byteSize
            createdAt
          }
          talentPoolMemberships {
            id
            talentPool {
              id
              name
            }
          }
        }
        addedBy {
          id
          fullName
        }
        outreachAttempts {
          id
          channel
          direction
          status
          createdVia
          messageContent
          responseContent
          sentAt
          respondedAt
          sentBy {
            id
            fullName
          }
          createdAt
        }
        candidateAssessment {
          id
          currentVersion {
            ${ASSESSMENT_VERSION_FOR_PANEL_FIELDS}
          }
        }
      }
    }
  }
`;

export const POOL_ROSTER_QUERY = gql`
  query PoolRoster(
    $id: ID!
    $interacted: Boolean
    $sortBy: PoolRosterSortEnum
    $sortDirection: SortDirectionEnum
    $first: Int
    $after: String
  ) {
    campaign(id: $id) {
      id
      pool {
        id
      }
      poolRosterSummary {
        total
        notYetProgressed
        returned
        inPipeline
        placed
        rejected
        withdrawn
      }
      poolRoster(
        interacted: $interacted
        sortBy: $sortBy
        sortDirection: $sortDirection
        first: $first
        after: $after
      ) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            kind
            status
            interacted
            lastInteractionAt
            reason
            returnedFromStage
            returnedAt
            source
            notes
            createdAt
            candidate {
              id
              referenceNumber
              firstName
              lastName
              fullName
              email
              currentRoleTitle
              currentCompany
              location
              linkedinUrl
              resumes {
                id
                filename
                url
                byteSize
                createdAt
              }
            }
            addedBy {
              id
              fullName
            }
          }
        }
      }
    }
  }
`;

export const ALL_CAMPAIGN_CANDIDATES_QUERY = gql`
  query AllCampaignCandidates($id: ID!) {
    campaign(id: $id) {
      id
      campaignCandidates {
        id
        stage
        createdAt
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        addedBy {
          id
          fullName
        }
        candidateAssessment {
          id
          currentVersion {
            id
            ${THRESHOLD_VERSION_FIELDS}
          }
        }
        candidateSelectionSteps {
          id
          continuityId
          round
          status
          outcome
          rating
          stepType
          label
        }
      }
      brief {
        id
        selectionProcess {
          id
          steps {
            id
            continuityId
            stepType
            label
            position
          }
        }
      }
    }
  }
`;

export const CAMPAIGN_CANDIDATE_DETAIL_QUERY = gql`
  query CampaignCandidateDetail($id: ID!) {
    campaignCandidate(id: $id) {
      id
      stage
      notes
      candidate {
        id
        referenceNumber
        fullName
        firstName
        lastName
        email
        phone
        linkedinUrl
        currentRoleTitle
        currentCompany
        location
      }
      campaign {
        id
        title
        roleBlueprint {
          id
          capabilityDimensions {
            id
            continuityId
            name
            idealScore
          }
        }
      }
      attachedNotes {
        id
        content
        noteType
        visibility
        author {
          id
          fullName
        }
        createdAt
      }
      outreachAttempts {
        id
        channel
        direction
        status
        createdVia
        messageContent
        responseContent
        sentAt
        respondedAt
        sentBy {
          id
          fullName
        }
        createdAt
      }
      outreachDrafts {
        id
        kind
        currentVersion {
          id
          versionNumber
          origin
          generationState
          messages { label body }
        }
      }
      interviewContributions {
        id
        kind
        candidateSelectionStepId
        occurredAt
        createdAt
        contributor {
          id
          fullName
        }
        note {
          interviewType
          rawNotes
          durationMinutes
          recordingNotes
        }
        transcript {
          content
          source
          originalFilename
          extractionState
        }
        assessmentFormSubmission {
          recommendation
          summary
          extractionState
          extractionError
          residualNotes
          importedFileUrl
          importedFileFilename
          reExtractable
        }
      }
      candidateSelectionSteps {
        id
        round
        status
        outcome
        rating
        stepType
        label
        scheduledAt
        interviewers
        clientFeedback
        competencyFeedback
        createdAt
      }
      candidateAssessment {
        id
        currentVersion {
          ${ASSESSMENT_VERSION_FOR_PANEL_FIELDS}
        }
      }
      latestSynthesisVersion {
        id
        roundNumber
        generationState
        content
        effectiveFrom
        author {
          id
          fullName
        }
      }
      offers {
        id
        status
        baseSalary
        currency
        startDate
        createdAt
      }
      portalComments {
        id
        content
        rating
        authorName
        createdAt
      }
    }
  }
`;

export const CANDIDATE_QUERY = gql`
  query Candidate($id: ID!) {
    candidate(id: $id) {
      id
      referenceNumber
      firstName
      lastName
      fullName
      email
      phone
      linkedinUrl
      currentRoleTitle
      currentCompany
      location
      notes
      consentStatus
      consentGivenAt
      consentNotes
      profileData
      resumes {
        id
        filename
        url
        byteSize
        createdAt
      }
      campaigns {
        id
        title
        status
      }
      campaignCandidates {
        id
        stage
        campaign {
          id
          title
          roleBlueprint {
            id
            capabilityDimensions {
              id
              continuityId
              name
              description
              position
              idealScore
              positiveIndicators
              negativeIndicators
            }
          }
        }
        candidateAssessment {
          id
          currentVersion {
            id
            overallScore
            recommendation
            overallSummary
            strengths
            concerns
            culturalFitNotes
            trajectoryNotes
            riskNotes
            versionNumber
            effectiveFrom
            assessor {
              id
              fullName
            }
            competencyScores {
              id
              score
              anchorLabel
              evidence
              notes
              detailedNotes
              capabilityDimension {
                id
                continuityId
                name
                description
                idealScore
              }
              behaviouralAnchors {
                id
                evidenceText
                sourceExcerpt
                sourceArtefactType
                sourceArtefactId
                aiGenerated
                createdAt
              }
            }
          }
        }
      }
      attachedNotes {
        id
        content
        noteType
        visibility
        author {
          id
          fullName
        }
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

// Fetched separately from CANDIDATE_QUERY so the heavy detail query (and its
// live useRecordChannel refresh) stays unchanged while the activity log loads
// alongside it.
export const CANDIDATE_ACTIVITY_QUERY = gql`
  query CandidateActivity($id: ID!) {
    candidate(id: $id) {
      id
      activityEvents {
        id
        kind
        occurredAt
        summary
        details
        source
        actorLabel
        actor {
          id
          fullName
        }
        campaign {
          id
          title
        }
      }
    }
  }
`;

// Deliver ▸ Interviews (#2230): the dynamic per-candidate selection-step
// journey, replacing the fixed client-interview list. Recruiter notes and the
// client interview guide are carried over unchanged.
export const SELECTION_JOURNEY_QUERY = gql`
  query SelectionJourney($id: ID!) {
    campaign(id: $id) {
      id
      currentStage
      roleBlueprint {
        id
        locked
      }
      interviewGuides {
        id
        selectionStepId
        stepType
        totalVersions
        currentVersion {
          id
          content
          versionNumber
          changeSummary
          effectiveFrom
          generationState
          generationError
          model
          roleBlueprintVersionNumber
          author {
            id
            fullName
          }
        }
        versions {
          id
          versionNumber
          changeSummary
          effectiveFrom
          author {
            id
            fullName
          }
        }
      }
      brief {
        id
        selectionProcess {
          id
          steps {
            id
            continuityId
            stepType
            label
            position
          }
        }
      }
      campaignCandidates(stage: "interview") {
        id
        stage
        createdAt
        selectionJourneyHalt {
          step {
            id
          }
          reason
        }
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        candidateAssessment {
          id
        }
        addedBy {
          id
          fullName
        }
        candidateSelectionSteps {
          id
          continuityId
          round
          status
          outcome
          rating
          stepType
          label
          candidatePrepNotes
          createdAt
          updatedAt
          interviewPackEligible
          assessmentFormEligible
          amended
          outcomeContradictsLaterActivity
          amendments {
            id
            eventType
            fromOutcome
            toOutcome
            fromRating
            toRating
            reason
            createdAt
            author {
              id
              fullName
            }
          }
          assessmentForm {
            id
            currentVersion {
              id
              versionNumber
            }
          }
          interviewPack {
            id
            generationState
            totalVersions
            currentVersion {
              id
              versionNumber
              generationState
              generationError
              changeSummary
              effectiveFrom
              content
              author {
                id
                fullName
              }
            }
          }
          interviewRoundSynthesis {
            id
            generationState
            assessmentState
            staleContributionCount
            totalVersions
            currentVersion {
              id
              versionNumber
              generationState
              generationError
              changeSummary
              effectiveFrom
              origin
              content
              author {
                id
                fullName
              }
              competencyScores {
                id
                capabilityDimensionId
                capabilityDimensionContinuityId
                dimensionName
                score
                evidence
                behaviouralAnchor
                anchorLabel
              }
            }
            versions {
              id
              versionNumber
              generationState
              changeSummary
              effectiveFrom
              effectiveTo
              origin
              author {
                id
                fullName
              }
            }
          }
        }
        interviewContributions {
          id
          kind
          candidateSelectionStepId
          occurredAt
          createdAt
          updatedAt
          contributor {
            id
            fullName
          }
          note {
            interviewType
            rawNotes
            durationMinutes
            recordingNotes
          }
          transcript {
            content
            source
            originalFilename
            extractionState
          }
          assessmentFormSubmission {
            recommendation
            summary
            extractionState
            extractionError
            residualNotes
            importedFileUrl
            importedFileFilename
            reExtractable
          }
        }
      }
    }
  }
`;

export const OFFER_STAGE_QUERY = gql`
  query OfferStage($id: ID!) {
    campaign(id: $id) {
      id
      currentStage
      campaignCandidates(stage: "offer") {
        id
        stage
        createdAt
        candidate {
          id
          referenceNumber
          fullName
          currentRoleTitle
          currentCompany
          location
        }
        addedBy {
          id
          fullName
        }
        offers {
          id
          status
          baseSalary
          currency
          bonusDetails
          equityDetails
          startDate
          notes
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const FIND_CANDIDATES_FOR_ROLE_QUERY = gql`
  query FindCandidatesForRole($campaignId: ID!, $limit: Int) {
    findCandidatesForRole(campaignId: $campaignId, limit: $limit) {
      id
      referenceNumber
      fullName
      email
      currentRoleTitle
      currentCompany
      location
    }
  }
`;

export const SEARCH_SIMILAR_CANDIDATES_QUERY = gql`
  query SearchSimilarCandidates($query: String!, $limit: Int) {
    searchSimilarCandidates(query: $query, limit: $limit) {
      id
      referenceNumber
      fullName
      email
      currentRoleTitle
      currentCompany
      location
    }
  }
`;

export const LONGLIST_EXECUTIVE_SUMMARY_QUERY = gql`
  query LonglistExecutiveSummary($campaignId: ID!) {
    longlistExecutiveSummary(campaignId: $campaignId) {
      campaignId
      campaignTitle
      markdown
    }
  }
`;

export const TALENT_POOLS_QUERY = gql`
  query TalentPools($onlyStandalone: Boolean, $first: Int, $after: String) {
    talentPools(onlyStandalone: $onlyStandalone, first: $first, after: $after) {
      edges {
        node {
          id
          name
          kind
          description
          candidateCount
          campaign {
            id
            title
            status
          }
          createdBy {
            id
            fullName
          }
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const TALENT_POOL_QUERY = gql`
  query TalentPool($id: ID!) {
    talentPool(id: $id) {
      id
      name
      kind
      description
      candidateCount
      campaign {
        id
        title
        status
      }
      createdBy {
        id
        fullName
      }
      memberships {
        id
        candidate {
          id
          referenceNumber
          fullName
          email
          currentRoleTitle
          currentCompany
          location
        }
        addedBy {
          id
          fullName
        }
        relevanceNotes
        createdAt
      }
      createdAt
    }
  }
`;

export const CANDIDATES_QUERY = gql`
  query Candidates(
    $search: String
    $sortBy: CandidatePoolSortEnum
    $sortDirection: SortDirectionEnum
    $stages: [String!]
    $consentStatuses: [String!]
    $activeSince: ISO8601DateTime
    $first: Int
    $after: String
  ) {
    candidates(
      search: $search
      sortBy: $sortBy
      sortDirection: $sortDirection
      stages: $stages
      consentStatuses: $consentStatuses
      activeSince: $activeSince
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          referenceNumber
          firstName
          lastName
          fullName
          email
          currentRoleTitle
          currentCompany
          location
          consentStatus
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const CANDIDATE_POOL_VIEWS_QUERY = gql`
  query CandidatePoolViews {
    candidatePoolViews {
      id
      name
      sortBy
      sortDirection
      search
      stages
      consentStatuses
      activeSince
    }
  }
`;

export const CANDIDATE_SEARCH_QUERY = gql`
  query CandidateSearch($search: String!, $excludeCampaignId: ID) {
    candidateSearch(search: $search, excludeCampaignId: $excludeCampaignId) {
      id
      referenceNumber
      fullName
      email
      currentRoleTitle
      currentCompany
      location
      campaignCandidates {
        id
        stage
        campaign {
          id
          title
        }
      }
      talentPoolMemberships {
        id
        talentPool {
          id
          name
        }
      }
    }
  }
`;

export const CANDIDATE_DUPLICATE_CHECK_QUERY = gql`
  query CandidateDuplicateCheck(
    $firstName: String!
    $lastName: String!
    $email: String
    $currentCompany: String
  ) {
    candidateDuplicateCheck(
      firstName: $firstName
      lastName: $lastName
      email: $email
      currentCompany: $currentCompany
    ) {
      id
      referenceNumber
      fullName
      email
      currentRoleTitle
      currentCompany
      location
      campaignCandidates {
        id
        stage
        campaign {
          id
          title
        }
      }
      talentPoolMemberships {
        id
        talentPool {
          id
          name
        }
      }
    }
  }
`;

export const DOCUMENT_TEMPLATES_QUERY = gql`
  query DocumentTemplates($templateType: String, $first: Int, $after: String) {
    documentTemplates(templateType: $templateType, first: $first, after: $after) {
      edges {
        node {
          id
          name
          templateType
          content
          branding
          isDefault
          createdAt
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const DOCUMENT_TEMPLATE_QUERY = gql`
  query DocumentTemplate($id: ID!) {
    documentTemplate(id: $id) {
      id
      name
      templateType
      content
      branding
      isDefault
      createdAt
      updatedAt
    }
  }
`;

export const PORTAL_ACTIVITIES_QUERY = gql`
  query PortalActivities {
    portalActivities {
      id
      eventType
      metadata
      campaignTitle
      candidateName
      portalUserName
      createdAt
    }
  }
`;

export const CAMPAIGN_LINKEDIN_JOB_POSTING_QUERY = gql`
  query CampaignLinkedinJobPosting($id: ID!) {
    campaign(id: $id) {
      id
      title
      linkedinJobPosting {
        id
        kind
        totalVersions
        currentVersion {
          id
          body
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
    }
  }
`;

// The Outreach drafts workspace (#3169): every candidate with their three
// per-channel drafts and their contact log, refreshed over the campaign channel.
export const CAMPAIGN_OUTREACH_WORKSPACE_QUERY = gql`
  query CampaignOutreachWorkspace($id: ID!) {
    campaign(id: $id) {
      id
      title
      brief { id }
      roleBlueprint { id }
      campaignCandidates {
        id
        stage
        candidate {
          id
          referenceNumber
          fullName
          email
          linkedinUrl
          currentRoleTitle
          currentCompany
        }
        emailMessage { ...OutreachDraftFields }
        linkedinMessages { ...OutreachDraftFields }
        linkedinConnectionRequest { ...OutreachDraftFields }
        outreachAttempts {
          id
          channel
          direction
          status
          createdVia
          messageContent
          responseContent
          sentAt
          respondedAt
          sentBy { id fullName }
          createdAt
        }
      }
    }
  }
  fragment OutreachDraftFields on CandidateOutreachMessage {
    id
    kind
    totalVersions
    currentVersion {
      id
      messages { label body }
      generationState
      generationError
      versionNumber
      origin
      changeSummary
    }
  }
`;

export const CAMPAIGN_BRANDED_JOB_POSTING_QUERY = gql`
  query CampaignBrandedJobPosting($id: ID!) {
    currentOrganisation {
      id
      name
      branding
      logoUrl
    }
    campaign(id: $id) {
      id
      title
      brandedJobPosting {
        id
        kind
        totalVersions
        currentVersion {
          id
          body
          generationState
          generationError
          model
          versionNumber
          effectiveFrom
          changeSummary
        }
      }
    }
  }
`;

export const CAMPAIGN_LINEAGE_QUERY = gql`
  query CampaignLineage($id: ID!) {
    campaign(id: $id) {
      id
      title
      lineage {
        nodes {
          id
          kind
          label
          subLabel
          variant
          artefactId
          currentVersionId
          currentVersionNumber
          totalVersionCount
          locked
          generationState
          stale
          pinnedBlueprintVersionNumber
          capabilityDimensionsCount
          aggregate {
            total
            fresh
            stale
            byBlueprintVersion {
              versionNumber
              count
            }
          }
        }
        edges {
          id
          from
          to
          kind
          state
          pinnedVersionNumber
          currentBlueprintVersionNumber
        }
      }
    }
  }
`;

export const ARTEFACT_VERSION_HISTORY_QUERY = gql`
  query ArtefactVersionHistory($id: ID!, $kind: ArtefactKindEnum!, $artefactId: ID!) {
    campaign(id: $id) {
      id
      artefactVersionHistory(kind: $kind, artefactId: $artefactId) {
        id
        versionNumber
        effectiveFrom
        effectiveTo
        changeSummary
        generationState
        origin
        actorSource
        model
        generationRunChatThreadId
        author {
          id
          fullName
        }
      }
    }
  }
`;

export const CHANGELOG_ENTRIES_QUERY = gql`
  query ChangelogEntries {
    changelogEntries {
      slug
      title
      content
      publishedAt
    }
  }
`;

export const UNSEEN_CHANGELOG_ENTRIES_QUERY = gql`
  query UnseenChangelogEntries {
    unseenChangelogEntries {
      slug
      title
      content
      publishedAt
    }
  }
`;
